/**
 * Refactored Authentication Orchestrator - Main Composition Root
 * Composes all auth modules to provide the same API as the monolithic orchestrator
 */

import { getCurrentInstance, onMounted, watch, ref } from 'vue';
import { useAuthFlow } from '@/composables/useAuthFlow';
import { useUserGetters, useUserActions } from '@/store';
import {
  useAuthChangePasswordMutation,
  useAuthAddPasswordToAccountMutation,
  useAuthSignUpMutation,
  useAuthLinkEmailMutation,
  useAuthLinkPhoneMutation,
  useAuthMigrateIdentityMutation,
  useUpdatePortfolioMutation,
  useAuthCheckUserExistsLazyQuery,
  useAuthCheckMergeEligibilityLazyQuery,
  useUserPortfolioLazyQuery,
  type CheckUserExistsInput,
  type CheckMergeEligibilityInput,
  type ChangePasswordInput,
  type AddPasswordToAccountInput,
  type SignUpInput,
  type LinkEmailInput,
  type LinkPhoneInput,
  type MigrateIdentityInput,
  type UpdatePortfolioInput
} from '@/generated/nexus-graphql';
import { onNexusLogin } from '@/apollo/nexusAuth';
import nexusClient from '@/apollo/nexusClient';
import { useAcquisitionMetadata } from '@/composables/useAcquisitionMetadata';
import { useVisitorId } from '@/composables/useVisitorId';
import { ENROLL_MUTATION, type EnrollResult } from '@/services/graphql/nexus/accessPolicy';
import { createAuthState } from './modules/useAuthState';
import { useAuthRecovery } from './modules/useAuthRecovery';
import { useAuthNavigation } from './modules/useAuthNavigation';
import { useAuthNavigationService } from './modules/useAuthNavigationService';
import { useAuthProfile } from './modules/useAuthProfile';
import { useAuthIdentity } from './modules/useAuthIdentity';
import { useAuthAlternateContact } from './modules/useAuthAlternateContact';
import { useAuthCore } from './modules/useAuthCore';
import { useAuthFlowControl } from './modules/useAuthFlowControl';
import { createDebugLogger } from './utils/debug';

const logger = createDebugLogger('AuthOrchestrator');

/**
 * Main orchestrator that composes all auth modules
 * Provides backward-compatible API with the monolithic orchestrator
 */
export function useAuthOrchestratorRefactored() {
  // Get dependencies
  const router = getCurrentInstance()?.proxy?.$router;
  const route = getCurrentInstance()?.proxy?.$route;
  const rootStore = getCurrentInstance()?.proxy?.$store;
  const { isProfileComplete, getUser } = useUserGetters(['isProfileComplete', 'getUser']);
  const { loginUser } = useUserActions(['loginUser']);

  // Initialize GraphQL mutations
  const { mutate: changePasswordMutate } = useAuthChangePasswordMutation({ clientId: 'nexus' });
  const { mutate: addPasswordMutate } = useAuthAddPasswordToAccountMutation({ clientId: 'nexus' });
  const { mutate: signUpMutate } = useAuthSignUpMutation({ clientId: 'nexus' });
  const { mutate: linkEmailMutate } = useAuthLinkEmailMutation({ clientId: 'nexus' });
  const { mutate: linkPhoneMutate } = useAuthLinkPhoneMutation({ clientId: 'nexus' });
  const { mutate: migrateIdentityMutate } = useAuthMigrateIdentityMutation({ clientId: 'nexus' });
  const { mutate: updatePortfolioMutate } = useUpdatePortfolioMutation({ clientId: 'nexus' });
  const { load: loadPortfolioQuery, result: portfolioData } = useUserPortfolioLazyQuery({
    clientId: 'nexus',
    fetchPolicy: 'network-only'
  });

  // Initialize checkUserExists lazy query at top level for reuse
  const checkUserExistsVars = ref<{ input: CheckUserExistsInput }>({
    input: { identifier: '', type: 'EMAIL' }
  });
  const {
    load: loadCheckUserExists,
    refetch: refetchCheckUserExists,
    result: checkUserExistsResult
  } = useAuthCheckUserExistsLazyQuery(checkUserExistsVars, {
    clientId: 'nexus',
    fetchPolicy: 'network-only'
  });

  // Initialize checkMergeEligibility lazy query for merge detection
  const checkMergeEligibilityVars = ref<{ input: CheckMergeEligibilityInput }>({
    input: { identifier: '', type: 'EMAIL', currentUserId: undefined }
  });
  const {
    load: loadCheckMergeEligibility,
    refetch: refetchCheckMergeEligibility,
    result: checkMergeEligibilityResult
  } = useAuthCheckMergeEligibilityLazyQuery(checkMergeEligibilityVars, {
    clientId: 'nexus',
    fetchPolicy: 'network-only'
  });

  // Create centralized state
  const { state, internalState, isOpen, resetState, clearError, currentRole, progress } =
    createAuthState();

  // Forward declare flowControl for navigation service callback
  let flowControl: ReturnType<typeof useAuthFlowControl>;

  // Create navigation service for unified phase transitions
  const navigationService = useAuthNavigationService(internalState, {
    onComplete: redirect => {
      // Call flowControl's completeAndRedirect when navigation completes
      if (flowControl?.completeAndRedirect) {
        flowControl.completeAndRedirect();
      } else if (redirect) {
        // Fallback if flowControl not yet initialized
        router?.push(redirect);
      }
    },
    onError: error => {
      logger.error('Navigation error:', error);
    }
  });

  // Initialize recovery module
  const recovery = useAuthRecovery(internalState);

  // Initialize auth flow composable
  const authFlow = useAuthFlow();
  const authFlowState = authFlow.state; // Correct property name

  // Create placeholder objects for circular dependencies
  /* eslint-disable prefer-const */
  let navigation: ReturnType<typeof useAuthNavigation>;
  let profile: ReturnType<typeof useAuthProfile>;
  /* eslint-enable prefer-const */

  /**
   * Mutation Wrappers
   *
   * These wrappers normalize Apollo's useMutation results into a shape expected by
   * the AuthDependencies interface. Input parameters use generated GraphQL input types.
   */

  // Type-safe adapter: Apollo errors to simplified { message: string }[]
  function mapApolloErrors(
    errors?: readonly { message?: string }[]
  ): Array<{ message: string }> | undefined {
    if (!errors) return undefined;
    return errors.map(e => ({ message: e.message ?? 'Unknown error' }));
  }

  const wrappedLinkEmailMutate = async (params: { input: LinkEmailInput }) => {
    const result = await linkEmailMutate(params);
    return { data: result?.data, errors: mapApolloErrors(result?.errors) };
  };

  const wrappedLinkPhoneMutate = async (params: { input: LinkPhoneInput }) => {
    const result = await linkPhoneMutate(params);
    return { data: result?.data, errors: mapApolloErrors(result?.errors) };
  };

  const wrappedMigrateIdentityMutate = async (params: { input: MigrateIdentityInput }) => {
    const result = await migrateIdentityMutate(params);
    return { data: result?.data, errors: mapApolloErrors(result?.errors) };
  };

  const wrappedUpdatePortfolioMutate = async (params: { input: UpdatePortfolioInput }) => {
    const result = await updatePortfolioMutate(params);
    return { data: result?.data, errors: mapApolloErrors(result?.errors) };
  };

  const wrappedSignUpMutate = async (params: { input: SignUpInput }) => {
    const result = await signUpMutate(params);
    return { data: result?.data, errors: mapApolloErrors(result?.errors) };
  };

  const wrappedChangePasswordMutate = async (params: { input: ChangePasswordInput }) => {
    const result = await changePasswordMutate(params);
    return { data: result?.data, errors: mapApolloErrors(result?.errors) };
  };

  const wrappedAddPasswordMutate = async (params: { input: AddPasswordToAccountInput }) => {
    const result = await addPasswordMutate(params);
    return { data: result?.data, errors: mapApolloErrors(result?.errors) };
  };

  // Initialize identity module first (since it has helper functions used by others)
  const identity = useAuthIdentity(internalState, {
    linkEmailMutate: wrappedLinkEmailMutate,
    linkPhoneMutate: wrappedLinkPhoneMutate,
    migrateIdentityMutate: wrappedMigrateIdentityMutate,
    getUser: getUser as any, // Cast Ref to ComputedRef
    portfolioData: portfolioData as any, // Cast Ref to ComputedRef
    rootStore,
    navigationService, // Pass unified navigation service
    nextPhase: () => navigation?.nextPhase() // Keep for compatibility
  });

  // Initialize profile module early (needed by flow control)
  // eslint-disable-next-line prefer-const
  profile = useAuthProfile(internalState, {
    updatePortfolioMutate: wrappedUpdatePortfolioMutate,
    rootStore,
    requestEmailLinking: identity.requestEmailLinking,
    requestPhoneLinking: identity.requestPhoneLinking,
    nextPhase: () => navigation?.nextPhase(), // Lazy evaluation
    completeAndRedirect: () => flowControl?.completeAndRedirect(), // Lazy evaluation
    navigationService // Pass unified navigation service
  });

  // Wrap loadPortfolioQuery to match expected signature
  const wrappedLoadPortfolioQuery = async () => {
    await loadPortfolioQuery();
    return { data: portfolioData.value, errors: undefined };
  };

  // Initialize flow control module
  flowControl = useAuthFlowControl(internalState, {
    router,
    route,
    rootStore,
    getUser: getUser as any, // Cast Ref to ComputedRef
    updatePortfolioMutate: wrappedUpdatePortfolioMutate,
    clearStoredProfileState: recovery.clearStoredProfileState,
    saveProfileStateToStorage: recovery.saveProfileStateToStorage,
    determineIncompletePhase: (userData, portfolioData) => {
      // Check if user has alternate contact (email or phone)
      const hasAlternateContact = identity.hasAlternateContact();
      const { alternateContactSkipped } = internalState;

      return profile.determineIncompletePhase(
        portfolioData?.userPortfolio?.completionStatus,
        userData?.userTypes || [],
        hasAlternateContact,
        alternateContactSkipped
      );
    },
    loadPortfolioQuery: wrappedLoadPortfolioQuery,
    navigationService // Pass unified navigation service
  });

  // Initialize navigation module with dependencies
  navigation = useAuthNavigation(internalState, {
    hasAlternateContact: identity.hasAlternateContact,
    completeProfileAndRedirect: flowControl.completeProfileAndRedirect
  });

  // Initialize core auth module
  const authCore = useAuthCore(internalState, {
    authFlow: {
      startAuthFlow: authFlow.start, // Correct method name
      verify: authFlow.verify,
      loginWithPassword: authFlow.loginWithPassword,
      forgotPassword: authFlow.forgotPassword,
      resend: authFlow.resend as any, // Cast to match expected type
      authFlowState
    },
    signUpMutate: wrappedSignUpMutate,
    changePasswordMutate: wrappedChangePasswordMutate,
    addPasswordMutate: wrappedAddPasswordMutate,
    onNexusLogin,
    loginUser,
    handleAuthComplete: flowControl.handleAuthComplete,
    navigationService, // Pass unified navigation service
    // Express enroll: persist the stashed name + 'None' role, then let
    // submitGeneralProfile → nextPhase('generalProfile', no real roles) →
    // completeProfileAndRedirect finish the flow. We set phase to
    // 'generalProfile' first so nextPhase resolves the completion branch.
    onExpressComplete: async () => {
      const general = (internalState.profileData.general || {}) as {
        firstName?: string;
        lastName?: string;
      };
      internalState.selectedUserTypes = ['None'];
      internalState.phase = 'generalProfile';
      await profile.submitGeneralProfile({
        firstName: general.firstName || '',
        lastName: general.lastName || '',
        phoneNumber: internalState.isEmail ? undefined : internalState.contact,
        userTypes: ['None']
      } as any);
    }
  });

  // Initialize alternate contact module
  const alternateContact = useAuthAlternateContact(internalState, {
    checkUserExistsQuery: async params => {
      // Update the reactive variables and execute the query
      checkUserExistsVars.value = params;
      // load() only works on first call; subsequent calls need refetch()
      const loadResult = await loadCheckUserExists();
      if (loadResult === false) {
        await refetchCheckUserExists();
      }
      return {
        data: checkUserExistsResult.value,
        errors: undefined // Query results don't have errors property
      };
    },
    checkMergeEligibilityQuery: async params => {
      // Update the reactive variables and execute the query
      checkMergeEligibilityVars.value = params;
      // load() only works on first call; subsequent calls need refetch()
      const loadResult = await loadCheckMergeEligibility();
      if (loadResult === false) {
        await refetchCheckMergeEligibility();
      }
      return {
        data: checkMergeEligibilityResult.value,
        errors: undefined // Query results don't have errors property
      };
    },
    linkEmailMutate: wrappedLinkEmailMutate,
    linkPhoneMutate: wrappedLinkPhoneMutate,
    authFlowHandlers: {
      refresh: async () => {
        // Refresh user state after account operations
        await loadPortfolioQuery();
      }
    },
    isProfileComplete,
    nextPhase: navigation.nextPhase,
    completeAndRedirect: flowControl.completeAndRedirect,
    navigationService, // Pass unified navigation service
    getUser: getUser as any // Cast to match expected type
  });

  // Set up watchers
  onMounted(() => {
    // Check for recovery on mount
    if (recovery.checkForRecovery()) {
      logger.log('Recovery available');
    }
  });

  // Watch for phase changes to save state
  watch(
    () => internalState.phase,
    newPhase => {
      // Define non-recoverable phases
      const nonRecoverablePhases = ['idle', 'complete', 'error', 'contact', 'otp', 'password'];

      // Save state for recoverable phases
      if (!nonRecoverablePhases.includes(newPhase)) {
        recovery.saveProfileStateToStorage();
        logger.log('Saved state for phase:', newPhase);
      } else if (newPhase === 'complete') {
        recovery.clearStoredProfileState();
        logger.log('Cleared state on completion');
      }
    }
  );

  /**
   * Anonymous "Join now" enrolment (access-policy floor:'off').
   *
   * One-tap join: no OTP, no identity. Collects client acquisition signals,
   * calls the `enroll` mutation with mode ANONYMOUS, then redirects to the
   * returned redirectUrl (falling back to sharableUrl). The backend returns the
   * /public-program/{slug} target.
   *
   * Sets state.loading / state.error so the dialog can reflect progress.
   * Returns the EnrollResult on success, or null on failure.
   */
  const { collect: collectAcquisitionMetadata } = useAcquisitionMetadata();
  const { getOrCreateVisitorId } = useVisitorId();

  const enrollAnonymous = async (programId: string): Promise<EnrollResult | null> => {
    if (!programId) {
      internalState.error = 'Missing program — cannot join.';
      return null;
    }
    internalState.loading = true;
    internalState.error = null;
    try {
      const metadata = collectAcquisitionMetadata();
      // Attribution: carry the anonymous visitor id so this join links back to
      // the share-link visit that produced it (view→join conversion).
      try {
        metadata.visitorId = getOrCreateVisitorId();
      } catch {
        /* non-fatal — enroll proceeds without attribution */
      }
      // eslint-disable-next-line no-console
      console.warn('[AP-DBG][enrollAnonymous] calling enroll(ANONYMOUS)', {
        programId,
        metadata
      });
      const { data } = await nexusClient.mutate<{ enroll: EnrollResult }>({
        mutation: ENROLL_MUTATION,
        variables: {
          input: {
            programId,
            mode: 'ANONYMOUS',
            metadata
          }
        }
      });

      const result = data?.enroll ?? null;
      // eslint-disable-next-line no-console
      console.warn('[AP-DBG][enrollAnonymous] enroll result', {
        studentId: result?.studentId,
        redirectUrl: result?.redirectUrl,
        sharableUrl: result?.sharableUrl
      });
      if (!result) {
        throw new Error('Enrollment failed — no result returned.');
      }

      // Anon-first /guide: an anonymous joiner has no auth session, so the
      // studentId IS their identity. Persist it keyed by program so the modern
      // /guide route can resolve the enrolled Student (ParticipantGuide reads
      // this when !user._id) — instead of falling back to the legacy
      // /public-program/{…studentId} route the backend redirectUrl points at.
      // Hard navigation (not router.push) so the fresh enrolled state loads
      // clean — same lesson as the in-place SPA auth reload.
      if (result.studentId) {
        try {
          localStorage.setItem(`pc_anon_student:${programId}`, result.studentId);
        } catch {
          /* non-fatal — falls back to the legacy redirect below */
        }
      }
      const href = result.studentId
        ? `${window.location.origin}/guide/${programId}/0`
        : (() => {
            // No studentId (shouldn't happen): fall back to the backend's
            // legacy /public-program redirect so the user still lands somewhere.
            const target = result.redirectUrl || result.sharableUrl;
            if (!target) return '';
            return target.startsWith('http')
              ? target
              : `${window.location.origin}${target.startsWith('/') ? '' : '/'}${target}`;
          })();
      if (href) {
        // Hard navigation initiated — keep the spinner up while the page
        // unloads (the finally below is guarded so loading stays true here).
        window.location.href = href;
      } else {
        // No target at all: the spinner would otherwise clear with nowhere to
        // go, leaving the user stuck. Surface a retryable error.
        internalState.error = 'Could not open the program. Please try again.';
      }
      return result;
    } catch (error) {
      logger.error('Anonymous enroll failed:', error);
      internalState.error =
        error instanceof Error ? error.message : 'Something went wrong joining the program.';
      return null;
    } finally {
      // On a successful redirect we intentionally leave loading=true so the
      // spinner persists while the browser navigates away. Only clear it when
      // no navigation was initiated (error / no-target cases).
      if (internalState.error) {
        internalState.loading = false;
      }
    }
  };

  // Return the composed API
  return {
    // State (readonly for external use)
    state,

    // Anonymous (access-policy) enrolment
    enrollAnonymous,

    // Expose isOpen separately for v-dialog compatibility
    isOpen,

    // Computed properties
    currentRole,
    progress,
    recoveryAvailable: recovery.checkForRecovery,

    // Flow Control Actions
    ...flowControl,

    // Navigation Actions
    ...navigation,

    // Recovery Actions
    ...recovery,

    // Core Auth Actions
    ...authCore,

    // Profile Actions
    ...profile,

    // Identity Actions
    ...identity,

    // Alternate Contact Actions
    ...alternateContact,

    // Utility functions
    clearError,
    resetAuth: resetState,

    // Debug helpers
    resetAuthFlow: flowControl.resetAuthFlow
  };
}

/**
 * Refactored Auth Orchestrator
 *
 * This refactored version breaks down the monolithic 2,500+ line orchestrator into:
 * - useAuthState (~150 lines): Centralized state management
 * - useAuthRecovery (~200 lines): Session recovery and persistence
 * - useAuthNavigation (~300 lines): Phase navigation logic
 * - useAuthFlowControl (~350 lines): Flow control and completion
 * - useAuthCore (~250 lines): Core authentication functions
 * - useAuthProfile (~400 lines): Profile management
 * - useAuthIdentity (~350 lines): Identity linking and merging
 * - useAuthAlternateContact (~200 lines): Alternate contact management
 *
 * Benefits:
 * - Each module has a single responsibility
 * - Files are now reviewable (150-400 lines each)
 * - Can be unit tested independently
 * - Multiple developers can work on different modules
 * - Easier to maintain and debug
 *
 * The API is 100% backward compatible with the original monolithic orchestrator.
 */
